import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload } from "@/lib/uploadLimits";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { AccountTagText, TAG_ICON_NAMES, TAG_ICONS, contrastVsWhite } from "@/components/tags/AccountTag";

const ICON_TYPES = [
  { value: "", label: "None" },
  { value: "lucide", label: "Icon" },
  { value: "emoji", label: "Emoji" },
  { value: "image", label: "Image" },
];

const BLANK = {
  name: "", color: "#0d9488", public: false, description: "",
  icon_type: "", icon_value: "", icon_public: false,
};

function IconPicker({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const set = (patch) => onChange({ ...value, ...patch });

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const chk = validateUpload(f, "tag_icons");
    if (!chk.ok) { toast.error(chk.message); return; }
    setUploading(true);
    try {
      const url = await uploadFile(f, "tag_icons");
      set({ icon_value: url });
    } catch { toast.error("Could not upload icon."); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-3" data-testid="tag-icon-picker">
      <div className="flex gap-1.5">
        {ICON_TYPES.map((t) => (
          <button key={t.value || "none"} type="button"
            onClick={() => set({ icon_type: t.value, icon_value: t.value === value.icon_type ? value.icon_value : "" })}
            className={`rounded-lg border px-3 py-1.5 text-sm font-[540] transition-colors ${value.icon_type === t.value ? "border-primary bg-teal-50 text-teal-700" : "border-border text-muted-foreground hover:bg-secondary"}`}
            data-testid={`tag-icon-type-${t.value || "none"}`}>{t.label}</button>
        ))}
      </div>

      {value.icon_type === "lucide" && (
        <div className="grid max-h-40 grid-cols-9 gap-1 overflow-y-auto rounded-lg border border-border p-2">
          {TAG_ICON_NAMES.map((name) => {
            const Ico = TAG_ICONS[name];
            const on = value.icon_value === name;
            return (
              <button key={name} type="button" onClick={() => set({ icon_value: name })} title={name}
                className={`flex h-8 w-8 items-center justify-center rounded ${on ? "bg-teal-50 text-teal-700 ring-1 ring-primary" : "text-foreground hover:bg-secondary"}`}
                data-testid={`tag-icon-${name}`}><Ico className="h-4 w-4" /></button>
            );
          })}
        </div>
      )}
      {value.icon_type === "emoji" && (
        <Input value={value.icon_value || ""} onChange={(e) => set({ icon_value: e.target.value.slice(0, 4) })} placeholder="🦦" className="w-24 text-center text-lg" data-testid="tag-icon-emoji" />
      )}
      {value.icon_type === "image" && (
        <div className="flex items-center gap-3">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
          {value.icon_value ? (
            <span className="relative inline-flex">
              <img src={fileUrl(value.icon_value)} alt="" className="h-9 w-9 rounded border border-border object-contain" />
              <button type="button" onClick={() => set({ icon_value: "" })} className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white"><X className="h-3 w-3" /></button>
            </span>
          ) : (
            <Button type="button" variant="secondary" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="tag-icon-upload">
              {uploading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />} Upload
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function AccountTagsManager() {
  const [tags, setTags] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, ...fields}
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/account-tags"); setTags(data); }
    catch { setTags([]); }
  };
  useEffect(() => { load(); }, []);

  const lowContrast = useMemo(() => editing && contrastVsWhite(editing.color) < 3, [editing]);

  const openNew = () => setEditing({ ...BLANK });
  const openEdit = (t) => setEditing({ ...t });

  const save = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name is required.");
    setSaving(true);
    const body = {
      name, color: editing.color || "#0d9488", public: !!editing.public,
      description: editing.description || "", icon_type: editing.icon_type || "",
      icon_value: editing.icon_value || "", icon_public: !!editing.icon_public,
    };
    try {
      if (editing.id) await api.patch(`/admin/account-tags/${editing.id}`, body);
      else await api.post("/admin/account-tags", body);
      toast.success(editing.id ? "Tag updated" : "Tag created");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save tag."); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/admin/account-tags/${t.id}`, { active: !t.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/admin/account-tags/${del.id}`); toast.success("Tag deactivated"); setDel(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!tags) return <Loading />;

  return (
    <Card className="border-0 p-5" data-testid="account-tags-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-[640] text-foreground">Account Tags</h2>
          <p className="text-xs text-muted-foreground">Applied to accounts (personal users) by admins. Public tags appear on profiles as colored text.</p>
        </div>
        <Button onClick={openNew} data-testid="account-tag-add"><Plus className="mr-1.5 h-4 w-4" /> Add tag</Button>
      </div>

      <div className="mt-4 divide-y divide-border/70">
        {tags.map((t) => (
          <div key={t.id} className={`flex items-center gap-3 py-2.5 ${t.active ? "" : "opacity-50"}`} data-testid={`account-tag-row-${t.key}`}>
            <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-border" style={{ backgroundColor: t.color }} />
            <div className="min-w-0 flex-1">
              <AccountTagText tag={t} inactive={!t.active} />
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className={`rounded px-1.5 py-0.5 font-[600] ${t.public ? "bg-teal-50 text-teal-700" : "bg-secondary text-muted-foreground"}`} data-testid={`account-tag-visibility-${t.key}`}>{t.public ? "PUBLIC" : "INTERNAL"}</span>
                <span className="font-mono">{t.key}</span>
                <span data-testid={`account-tag-usage-${t.key}`}>· {t.user_count} {t.user_count === 1 ? "user" : "users"}</span>
              </div>
            </div>
            <Switch checked={!!t.active} onCheckedChange={() => toggleActive(t)} data-testid={`account-tag-active-${t.key}`} />
            <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`account-tag-edit-${t.key}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDel(t)} data-testid={`account-tag-delete-${t.key}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {tags.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No tags yet.</p>}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="account-tag-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit tag" : "New tag"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="account-tag-name" placeholder="e.g. Beta Tester" />
              </div>
              <div className="space-y-1.5">
                <Label>Color</Label>
                <div className="flex items-center gap-3">
                  <input type="color" value={editing.color || "#0d9488"} onChange={(e) => setEditing((s) => ({ ...s, color: e.target.value }))} className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent p-0.5" data-testid="account-tag-color" />
                  <Input value={editing.color || ""} onChange={(e) => setEditing((s) => ({ ...s, color: e.target.value }))} className="w-32 font-mono" />
                  <AccountTagText tag={{ ...editing, key: "preview" }} />
                </div>
                {lowContrast && <p className="text-xs font-[560] text-amber-600" data-testid="account-tag-contrast-warning">⚠ Low contrast on white — this may be hard to read. Pick a darker color.</p>}
              </div>
              <div className="rounded-lg border border-border p-3">
                <Label className="mb-2 block">Icon (optional)</Label>
                <IconPicker value={editing} onChange={(v) => setEditing((s) => ({ ...s, ...v }))} />
              </div>
              <label className="flex items-center gap-2.5">
                <Switch checked={!!editing.public} onCheckedChange={(v) => setEditing((s) => ({ ...s, public: v }))} data-testid="account-tag-public" />
                <span className="text-sm">Public tag text <span className="text-muted-foreground">— shows the tag name on the member&apos;s public profile (otherwise Internal/admin-only)</span></span>
              </label>
              {editing.icon_type && editing.icon_value && (
                <label className="flex items-center gap-2.5">
                  <Switch checked={!!editing.icon_public} onCheckedChange={(v) => setEditing((s) => ({ ...s, icon_public: v }))} data-testid="account-tag-icon-public" />
                  <span className="text-sm">Public icon/emoji <span className="text-muted-foreground">— shows the icon/emoji next to the tag on public profiles (needs &ldquo;Public tag text&rdquo; on too)</span></span>
                </label>
              )}
              <div className="space-y-1.5">
                <Label>Description <span className="text-muted-foreground">(internal)</span></Label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} data-testid="account-tag-description" placeholder="What is this tag for?" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="account-tag-save">{saving ? "Saving…" : "Save tag"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft-delete confirm */}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate “{del?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>The tag is soft-deleted: it stays on members who already have it (greyed in admin), is hidden on public profiles, and is excluded from gating. You can reactivate it anytime with the toggle.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} data-testid="account-tag-delete-confirm">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default AccountTagsManager;
